import firebaseService from '../services/firebase/FirebaseService';
import { doc, getDoc, setDoc } from 'firebase/firestore';

/**
 * Chat Encryption Utility (Web)
 * Provides end-to-end encryption (E2EE) for chat messages using AES-256-GCM
 * Uses Web Crypto API which is available in modern browsers
 */

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256; // bits
const IV_LENGTH = 12; // bytes for GCM
const TAG_LENGTH = 128; // bits for authentication tag

/**
 * Interface for encrypted message
 */
export interface EncryptedMessage {
  encryptedText: string; // base64 encoded encrypted data
  iv: string; // base64 encoded initialization vector
  tag: string; // base64 encoded authentication tag
}

/**
 * Generate a random encryption key
 */
export const generateEncryptionKey = async (): Promise<string> => {
  try {
    // Generate random bytes for the key (256 bits = 32 bytes)
    const keyBytes = new Uint8Array(32);
    crypto.getRandomValues(keyBytes);

    // Convert to base64 for storage
    const keyBase64 = btoa(String.fromCharCode(...Array.from(keyBytes)));
    return keyBase64;
  } catch (error) {
    throw new Error('Failed to generate encryption key');
  }
};

/**
 * Get or create encryption key for a chat
 * Keys are stored in localStorage (consider using IndexedDB or sessionStorage for better security)
 */
export const getChatEncryptionKey = async (chatId: string): Promise<string> => {
  try {
    const storageKey = `chat_key_${chatId}`;

    // Try to retrieve existing key from localStorage
    const storedKey = localStorage.getItem(storageKey);
    if (storedKey) {
      return storedKey;
    }

    // Try fetching shared key from Firestore
    const firestoreKey = await fetchKeyFromFirestore(chatId);
    if (firestoreKey) {
      localStorage.setItem(storageKey, firestoreKey);
      return firestoreKey;
    }

    // As a fallback (older chats), generate a new key, persist, and cache
    const newKey = await generateEncryptionKey();
    await persistKeyToFirestore(chatId, newKey);
    localStorage.setItem(storageKey, newKey);
    return newKey;
  } catch (error) {
    throw new Error('Failed to get chat encryption key');
  }
};

const fetchKeyFromFirestore = async (chatId: string): Promise<string | null> => {
  try {
    const firestore = firebaseService.getFirestore();
    const chatRef = doc(firestore, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);

    if (chatSnap.exists()) {
      const data = chatSnap.data();
      const existingKey = data?.encryptionKey;
      if (existingKey && typeof existingKey === 'string') {
        return existingKey;
      }
    }
  } catch (error) {}
  return null;
};

const persistKeyToFirestore = async (chatId: string, key: string): Promise<void> => {
  const firestore = firebaseService.getFirestore();
  const chatRef = doc(firestore, 'chats', chatId);
  await setDoc(
    chatRef,
    {
      encryptionKey: key,
    },
    { merge: true },
  );
};

/**
 * Convert base64 key to CryptoKey for Web Crypto API
 */
const importKey = async (keyBase64: string): Promise<CryptoKey> => {
  try {
    // Decode base64 to bytes
    const keyBytes = new Uint8Array(Array.from(atob(keyBase64), c => c.charCodeAt(0)));

    // Import key for AES-GCM
    return await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: ALGORITHM },
      false, // not extractable
      ['encrypt', 'decrypt'],
    );
  } catch (error) {
    throw new Error('Failed to import encryption key');
  }
};

/**
 * Encrypt a message using AES-256-GCM
 */
export const encryptMessage = async (text: string, chatId: string): Promise<EncryptedMessage> => {
  try {
    // Get encryption key for this chat
    const keyBase64 = await getChatEncryptionKey(chatId);
    const key = await importKey(keyBase64);

    // Generate random IV
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

    // Convert text to bytes
    const textEncoder = new TextEncoder();
    const textBytes = textEncoder.encode(text);

    // Encrypt
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: ALGORITHM,
        iv: iv,
        tagLength: TAG_LENGTH,
      },
      key,
      textBytes,
    );

    // Extract encrypted data and authentication tag
    // GCM includes authentication tag at the end
    const tagLengthBytes = TAG_LENGTH / 8;
    const encryptedBytes = new Uint8Array(encryptedData.slice(0, -tagLengthBytes));
    const tagBytes = new Uint8Array(encryptedData.slice(-tagLengthBytes));

    // Convert to base64 for storage
    const encryptedText = btoa(String.fromCharCode(...Array.from(encryptedBytes)));
    const ivBase64 = btoa(String.fromCharCode(...Array.from(iv)));
    const tagBase64 = btoa(String.fromCharCode(...Array.from(tagBytes)));

    return {
      encryptedText,
      iv: ivBase64,
      tag: tagBase64,
    };
  } catch (error) {
    throw new Error('Failed to encrypt message');
  }
};

/**
 * Decrypt a message using AES-256-GCM
 */
export const decryptMessage = async (
  encryptedMessage: EncryptedMessage,
  chatId: string,
): Promise<string> => {
  try {
    // Get decryption key for this chat
    const keyBase64 = await getChatEncryptionKey(chatId);
    const key = await importKey(keyBase64);

    // Decode base64 to bytes
    const encryptedBytes = new Uint8Array(
      Array.from(atob(encryptedMessage.encryptedText), c => c.charCodeAt(0)),
    );
    const iv = new Uint8Array(Array.from(atob(encryptedMessage.iv), c => c.charCodeAt(0)));
    const tag = new Uint8Array(Array.from(atob(encryptedMessage.tag), c => c.charCodeAt(0)));

    // Combine encrypted data and tag
    const encryptedDataWithTag = new Uint8Array(encryptedBytes.length + tag.length);
    encryptedDataWithTag.set(encryptedBytes);
    encryptedDataWithTag.set(tag, encryptedBytes.length);

    // Decrypt
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: ALGORITHM,
        iv: iv,
        tagLength: TAG_LENGTH,
      },
      key,
      encryptedDataWithTag,
    );

    // Convert bytes to text
    const textDecoder = new TextDecoder();
    return textDecoder.decode(decryptedData);
  } catch (error) {
    throw new Error('Failed to decrypt message');
  }
};

/**
 * Encrypt message text for storage
 * Returns a JSON string that can be stored in Firestore
 */
export const encryptMessageForStorage = async (text: string, chatId: string): Promise<string> => {
  const encrypted = await encryptMessage(text, chatId);
  return JSON.stringify(encrypted);
};

/**
 * Decrypt message text from storage
 * Takes a JSON string from Firestore and returns decrypted text
 */
export const decryptMessageFromStorage = async (
  encryptedJson: string,
  chatId: string,
): Promise<string> => {
  const encrypted = JSON.parse(encryptedJson) as EncryptedMessage;
  return decryptMessage(encrypted, chatId);
};
